import { useRef } from 'react';
import { ACCESS_BY_PROJECT } from '../data/portfolio';
import useHoverOnScroll from '../hooks/useHoverOnScroll';
import useWheelScroll from '../hooks/useWheelScroll';
import useEntryStagger from '../hooks/useEntryStagger';
import useIsMobile from '../hooks/useIsMobile';
import {
    FADE_MASK,
    MOBILE_FADE_MASK,
    LIST_PAD_TOP,
    LIST_PAD_BOTTOM,
    PANEL_TOP,
    PANEL_BOTTOM,
    ROW_PAD_Y,
    MOBILE_EDGE,
    MOBILE_PANEL_LEFT,
    MOBILE_PANEL_TOP,
    MOBILE_PANEL_BOTTOM,
    MOBILE_LIST_PAD,
    MOBILE_ROW_PAD_Y,
} from '../theme';

const NO_HOVER = () => {};

export default function SkillsPanel({ skills, hover, onHover, shown, palette, onProjectClick }) {
    const mobile = useIsMobile();
    // Nothing hovers on touch, so hover is never entered there: every row stays
    // fully legible and only the project badges respond to a tap.
    const trackHover = mobile ? NO_HOVER : onHover;

    const scrollRef = useRef(null);
    useHoverOnScroll(scrollRef, trackHover);
    useWheelScroll(scrollRef, shown);
    const staggered = useEntryStagger(shown, hover);

    return (
        <div
            inert={!shown}
            aria-hidden={!shown}
            style={{
                position: 'absolute',
                right: `${mobile ? MOBILE_EDGE : 68}px`,
                left: mobile ? `${MOBILE_PANEL_LEFT}px` : undefined,
                top: mobile ? `calc(${MOBILE_PANEL_TOP}px + env(safe-area-inset-top, 0px))` : `${PANEL_TOP}px`,
                bottom: mobile ? `calc(${MOBILE_PANEL_BOTTOM}px + env(safe-area-inset-bottom, 0px))` : `${PANEL_BOTTOM}px`,
                width: mobile ? 'auto' : 'min(460px,44vw)',
                display: 'flex',
                flexDirection: 'column',
                textAlign: mobile ? 'left' : 'right',
                pointerEvents: shown ? 'auto' : 'none',
                opacity: shown ? 1 : 0,
                transform: shown ? 'translateY(0)' : 'translateY(14px)',
                transition: shown
                    ? 'opacity .55s ease, transform .55s ease'
                    : 'opacity .3s ease, transform .3s ease',
            }}
        >
            <div
                ref={scrollRef}
                onMouseLeave={() => trackHover(null)}
                style={{
                    flex: '1 1 auto',
                    minHeight: 0,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    WebkitOverflowScrolling: 'touch',
                    paddingRight: mobile ? '0' : '8px',
                    paddingTop: `${mobile ? MOBILE_LIST_PAD : LIST_PAD_TOP}px`,
                    paddingBottom: `${mobile ? MOBILE_LIST_PAD : LIST_PAD_BOTTOM}px`,
                    scrollbarWidth: 'none',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: mobile ? 'flex-start' : 'flex-end',
                    textAlign: mobile ? 'left' : 'right',
                    maskImage: mobile ? MOBILE_FADE_MASK : FADE_MASK,
                    WebkitMaskImage: mobile ? MOBILE_FADE_MASK : FADE_MASK,
                }}
            >
                {skills.map((s, i) => {
                    const isHover = hover === s.name;
                    const dimmed = !mobile && hover != null && !isHover;
                    // Touch has no hover, so what hovering reveals is simply always
                    // revealed there, rather than hidden behind a gesture nobody can make.
                    const lit = isHover || mobile;
                    // One size for every skill, as on the project list. The order
                    // already carries how much each one is used, so the type no
                    // longer has to say it a second time.
                    const size = mobile ? '24px' : 'clamp(22px,2.6vw,34px)';

                    return (
                        <div
                            key={s.name}
                            onMouseEnter={() => trackHover(s.name)}
                            data-hover-id={s.name}
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: mobile ? 'flex-start' : 'flex-end',
                                gap: '4px',
                                // Full width, so the hover band covers the row rather than just
                                // the text; the parent's flex-end would otherwise shrink it.
                                alignSelf: 'stretch',
                                padding: `${mobile ? MOBILE_ROW_PAD_Y : ROW_PAD_Y}px 0`,
                                cursor: 'default',
                                opacity: shown ? (dimmed ? 0.3 : 1) : 0,
                                transform: shown ? 'translateY(0)' : 'translateY(10px)',
                                transition: 'opacity .5s ease, transform .5s ease',
                                transitionDelay: staggered ? `${(i * 0.05).toFixed(2)}s` : '0s',
                            }}
                        >
                            <div
                                style={{
                                    fontFamily: "'Neue Montreal',sans-serif",
                                    fontWeight: 300,
                                    fontSize: size,
                                    lineHeight: 1,
                                    letterSpacing: '.005em',
                                    whiteSpace: mobile ? 'normal' : 'nowrap',
                                    color: isHover ? 'var(--accent)' : 'inherit',
                                    borderBottom: isHover
                                        ? '1px solid color-mix(in oklab, var(--accent) 55%, transparent)'
                                        : '1px solid transparent',
                                    paddingBottom: '2px',
                                    transition: 'color .25s ease',
                                }}
                            >
                                {s.name}
                            </div>

                            {s.desc && (
                                <div
                                    style={{
                                        maxWidth: mobile ? 'none' : '340px',
                                        marginTop: '4px',
                                        fontFamily: "'Hanken Grotesk',sans-serif",
                                        // Italic and looser than the badges below, so prose and
                                        // labels don't read as one run of text.
                                        fontStyle: 'italic',
                                        fontSize: mobile ? '14px' : '12.5px',
                                        fontWeight: 400,
                                        lineHeight: 1.65,
                                        letterSpacing: '.005em',
                                        opacity: lit ? 0.72 : 0.36,
                                        transition: 'opacity .25s ease',
                                    }}
                                >
                                    {s.desc}
                                </div>
                            )}

                            <div
                                style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                        justifyContent: mobile ? 'flex-start' : 'flex-end',
                                    gap: '5px 16px',
                                        maxWidth: mobile ? 'none' : '360px',
                                    // Clear air under the prose, so the badges read as a
                                    // separate band rather than a continuation of the sentence.
                                    marginTop: s.desc ? '11px' : '2px',
                                }}
                            >
                                {s.uses.map((use) => {
                                    const access = ACCESS_BY_PROJECT[use] || 'private';
                                    const dot = palette.status[access];
                                    return (
                                        <button
                                            type="button"
                                            key={use}
                                            className="sig-skill-badge"
                                            onClick={(event) => {
                                                // Opening Projects installs its outside-click listener.
                                                // Keep this same click from immediately closing the row.
                                                event.stopPropagation();
                                                onProjectClick(use);
                                            }}
                                            title={`Open ${use}`}
                                            style={{
                                                display: 'inline-flex',
                                                background: 'none', border: 0, padding: mobile ? '5px 0' : 0,
                                                textAlign: 'left', minHeight: mobile ? '36px' : undefined,
                                                alignItems: 'center',
                                                gap: '6px',
                                                fontFamily: "'Hanken Grotesk',sans-serif",
                                                fontSize: '11px',
                                                // Heavier and wider-tracked than the prose above, which
                                                // is what makes these read as labels.
                                                fontWeight: 600,
                                                letterSpacing: '.06em',
                                                // No inline `color`: it would outrank the class's hover
                                                // rule. Inherits from the panel either way.
                                                opacity: lit ? 0.92 : 0.48,
                                                transition: 'opacity .25s ease',
                                            }}
                                        >
                                            <span
                                                style={{
                                                    width: '6px',
                                                    height: '6px',
                                                    borderRadius: '50%',
                                                    flex: '0 0 auto',
                                                    background: dot,
                                                    boxShadow: access !== 'private' ? `0 0 6px ${dot}` : 'none',
                                                }}
                                            />
                                            {use}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
